import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import CountyMap from './CountyMap';
import { useData } from '../hooks/useData';
import FrequencyChart from './FrequencyChart';
import RecencyChart from './RecencyChart';
import PrevalenceJitterPlot from './PrevalenceJitterPlot';
import ClusteringPlot from './ClusteringPlot';
import { loadClusteringData } from '../utils/loadClusterData';
import { loadIndianaAverages } from '../utils/loadIndianaAverages';

function Dashboard() {
  const { geoData, countyData, years, touchpointTypes, stateData } = useData();
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedYear, setSelectedYear] = useState('Last Two Years');
  const [selectedFeature, setSelectedFeature] = useState('prevalence');
  const [clusteringData, setClusteringData] = useState({});
  const [indianaAverages, setIndianaAverages] = useState(null);
  const [showGlyphs, setShowGlyphs] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const [clusterData, avgData] = await Promise.all([
        loadClusteringData(),
        loadIndianaAverages()
      ]);
      setClusteringData(clusterData);
      setIndianaAverages(avgData);
    };
    loadData();
  }, []);

  const handleCountySelect = (countyFeature) => {
    // match 5-digit geojson fips with 1-3 digit csv fips
    const fullGeoJsonFips = countyFeature.properties.id;
    const countyCodeString = fullGeoJsonFips.substring(2);
    const countyCodeNumber = parseInt(countyCodeString, 10);
    const matchingKey = Object.keys(countyData).find(key => parseInt(key, 10) === countyCodeNumber);

    if (matchingKey && countyData[matchingKey]) {
      setSelectedCounty(countyData[matchingKey]);
    } else {
      console.warn(`No touchpoint data found for FIPS: ${countyCodeNumber}`);
      setSelectedCounty(null);
    }
  };

  // handle county selection from clustering plot
  const handleClusteringCountySelect = (fips) => {
    if (countyData[fips]) {
      setSelectedCounty(countyData[fips]);
    } else {
      console.warn(`No touchpoint data found for FIPS: ${fips}`);
      setSelectedCounty(null);
    }
  };

  return (
    <div className="dashboard">
      {/* clustering + map */}
      <div className="dashboard-top">
        <div className="dashboard-box clustering-box">
          <div className="chart-header">
            <h3>Clustering Analysis</h3>
            <div className="info-button-wrapper">
              <span className="info-button">i</span>
              <span className="info-tooltip">Visual representation of county similarities based on touchpoint patterns.<br /><strong>Click points to view individual county data.</strong></span>
            </div>
          </div>
          <div className="clustering-content">
            <div className="clustering-control-panel">
              <div className="control-section">
                <span className="control-label">Feature</span>
                <div className="feature-toggle-group">
                  <button
                    className={`feature-toggle-btn ${selectedFeature === 'prevalence' ? 'active' : ''}`}
                    onClick={() => setSelectedFeature('prevalence')}
                  >
                    Prevalence
                  </button>
                  <button
                    className={`feature-toggle-btn ${selectedFeature === 'frequency' ? 'active' : ''}`}
                    onClick={() => setSelectedFeature('frequency')}
                  >
                    Frequency
                  </button>
                  <button
                    className={`feature-toggle-btn ${selectedFeature === 'recency' ? 'active' : ''}`}
                    onClick={() => setSelectedFeature('recency')}
                  >
                    Recency
                  </button>
                </div>
              </div>
              <div className="control-section">
                <span className="control-label">Year</span>
                <select
                  className="control-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="control-section">
                <span className="control-label">Selected</span>
                <span className="selected-county-display">
                  {selectedCounty ? selectedCounty.name : 'Indiana (All)'}
                </span>
              </div>
            </div>
            <div className="clustering-plot-container">
              <ClusteringPlot
                clusteringData={clusteringData[selectedYear] || []}
                selectedCountyFips={selectedCounty ? selectedCounty.fips : null}
                onCountySelect={handleClusteringCountySelect}
                selectedFeature={selectedFeature}
              />
            </div>
          </div>
        </div>
        <div className="dashboard-box map-box">
          <div className="chart-header">
            <h3>Indiana Counties Map</h3>
            <div className="header-controls">
              <button 
                className="glyph-toggle-button"
                onClick={() => setShowGlyphs(!showGlyphs)}
              >
                {showGlyphs ? 'Hide Touchpoint Glyphs' : 'Show Touchpoint Glyphs'}
              </button>
              <div className="info-button-wrapper">
                <span className="info-button">i</span>
                <span className="info-tooltip">Interactive map showing all Indiana counties.<br />Zoom in to view county names.<br /><strong>Click county on map to view individual county data.</strong><br /><br /><strong>Touchpoint Glyphs:</strong> When enabled, shows radial charts at each county center. Each petal represents a touchpoint type. Petal length shows prevalence (% of decedents), brightness shows recency (darker = more recent).</span>
              </div>
            </div>
          </div>
          <div className="county-selector">
            <label htmlFor="county-dropdown">Select County: </label>
            <select
              id="county-dropdown"
              value={selectedCounty ? selectedCounty.fips : ''}
              onChange={(e) => {
                const fips = e.target.value;
                if (fips === '') {
                  setSelectedCounty(null);
                } else {
                  handleClusteringCountySelect(fips);
                }
              }}
            >
              <option value="">Indiana (All Counties)</option>
              {countyData && Object.keys(countyData).sort((a, b) => {
                return countyData[a].name.localeCompare(countyData[b].name);
              }).map(fips => (
                <option key={fips} value={fips}>
                  {countyData[fips].name}
                </option>
              ))}
            </select>
          </div>
          <div className="map-container">
            <CountyMap
              geoData={geoData}
              onCountySelect={handleCountySelect}
              selectedCountyFips={selectedCounty ? selectedCounty.fips : null}
              countyData={countyData}
              selectedYear={selectedYear}
              touchpointTypes={touchpointTypes}
              showGlyphs={showGlyphs}
            />
          </div>
        </div>
      </div>

      {/* metric views */}
      <div className="dashboard-bottom">
        <div className="dashboard-box metric-box">
          <div className="chart-header">
            <h3>Prevalence</h3>
            <div className="info-button-wrapper">
              <span className="info-button">i</span>
              <span className="info-tooltip">Percentage of overdose decedents who had each touchpoint type in the 12 months before death.<br /><strong>Click points to view individual county data.</strong></span>
            </div>
          </div>
          <PrevalenceJitterPlot
            allCountyData={countyData}
            stateData={stateData}
            selectedCountyFips={selectedCounty ? selectedCounty.fips : null}
            touchpointTypes={touchpointTypes}
            selectedYear={selectedYear}
            onCountySelect={handleClusteringCountySelect}
          />
        </div>
        <div className="dashboard-box metric-box">
          <div className="chart-header">
            <h3>Frequency Over Time</h3>
            <div className="info-button-wrapper">
              <span className="info-button">i</span>
              <span className="info-tooltip">Average number of times decedents interacted with each touchpoint type per year.</span>
            </div>
          </div>
          <FrequencyChart
            countyData={selectedCounty}
            indianaAverages={indianaAverages}
            years={years}
            touchpointTypes={touchpointTypes}
          />
        </div>
        <div className="dashboard-box metric-box">
          <div className="chart-header">
            <h3>Recency (Avg. Days Before Death)</h3>
            <div className="info-button-wrapper">
              <span className="info-button">i</span>
              <span className="info-tooltip">Average number of days between the last touchpoint interaction and death.<br />Bar opacity indicates sample size (n). Black line represents state average.</span>
            </div>
          </div>
          <RecencyChart
            countyData={selectedCounty}
            indianaAverages={indianaAverages}
            touchpointTypes={touchpointTypes}
            selectedYear={selectedYear}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
